import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useFirstAnalyses from 'hooks/useFirstAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { firstAnalysesSelectors } from 'features/firstAnalyses';

const FirstAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { first_analysis_id } = useParams();
  const { getFirstAnalysis, clearLocalActiveFirstAnalysis } = useFirstAnalyses();
  const { getFirstAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const firstAnalysisFetching = useCustomSelector(firstAnalysesSelectors.firstAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchFirstAnalysis = useCallback((id) => {
    setOpen(true);
    getFirstAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { first_analysis } } = payload;
        handleSetData(first_analysis);
      }
    })
  }, [getFirstAnalysis]);

  const handleSetData = (first_analysis) => {
    setInput(first_analysis?.input);
    setResult(first_analysis?.result);
  };

  const handleExiting = () => {
    setInput('');
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl11s`);
    clearLocalActiveFirstAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchFirstAnalysesEvents = useCallback((offset, first_analysis_id) => {
    getFirstAnalysesEvents({ offset, first_analysis_id })
  }, [ getFirstAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchFirstAnalysesEvents(0, first_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!first_analysis_id) fetchFirstAnalysis(first_analysis_id);
  }, [first_analysis_id, fetchFirstAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { firstAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {first_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Input'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={input ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={first_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchFirstAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <FirstAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);